import * as React from 'react';
import type { useTranslation } from 'react-i18next';

/**
 * Reorder cart R1: the patient-facing "Add to your order" box for cross-sell.
 *
 * Purely additive to the reorder Confirm step - the parent renders it only
 * when the token carries a cross_kind, so a plain reorder is unchanged.
 *
 * PRICING: every number shown here is SERVER-computed (get_patient_public_view
 * -> verify-qr-token). This component only PICKS which server number applies
 * by anchor quantity (via reorderCrossUnit upstream) and displays it. It never
 * derives a discount (v2.1 doctrine).
 */

const IMAGES: Record<'sandal' | 'insole', string[]> = {
  sandal: [
    '/products/sandals/buckle.JPG',
    '/products/sandals/velcro.JPG',
    '/products/sandals/flipflop.JPG',
  ],
  insole: [
    '/products/custom-made-insoles/cns-eva.jpg',
    '/products/custom-made-insoles/cns-pa.png',
    '/products/custom-made-insoles/cns-tpu.png',
  ],
};

const ROTATE_MS = 3500;

function formatAed(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Self-contained crossfade image rotator. No external deps. Pauses on hover
 *  and when the tab is hidden; disabled entirely under prefers-reduced-motion.
 *  A fade (not a slide) so it is direction-agnostic and works in RTL as-is. */
function ImageRotator({ srcs, alt }: { srcs: string[]; alt: string }) {
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const reduceMotion = React.useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  React.useEffect(() => {
    if (reduceMotion || paused || srcs.length <= 1) return undefined;
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    const id = window.setInterval(() => setIdx((i) => (i + 1) % srcs.length), ROTATE_MS);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduceMotion, paused, srcs.length]);

  return (
    <div
      className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-white"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {srcs.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === idx ? alt : ''}
          aria-hidden={i === idx ? undefined : true}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
          style={{ opacity: i === idx ? 1 : 0 }}
          loading="lazy"
        />
      ))}
    </div>
  );
}

export function CrossSellBox({
  crossKind,
  unitAed,
  fullAed,
  discountedAed,
  discountApplies,
  unlockAtAnchorQty,
  anchorQty,
  crossQty,
  setCrossQty,
  maxQty,
  t,
}: {
  crossKind: 'sandal' | 'insole';
  unitAed: number;
  fullAed: number | null;
  discountedAed: number | null;
  discountApplies: boolean;
  unlockAtAnchorQty: number | null;
  anchorQty: number;
  crossQty: number;
  setCrossQty: (n: number) => void;
  maxQty: number;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const label =
    crossKind === 'sandal'
      ? t('reorder_cross_sandals_label', 'Custom orthopaedic sandals')
      : t('reorder_cross_insoles_label', 'Custom insoles');
  // The real discount percent, always from full vs discounted (both server-
  // emitted), so the applied badge AND the unlock hint show the same truth.
  const pct =
    fullAed && fullAed > 0 && discountedAed && discountedAed < fullAed
      ? Math.round((1 - discountedAed / fullAed) * 100)
      : 0;
  // Sandals on an insole anchor: the discount unlocks at 2+ anchor pairs.
  const canUnlock = crossKind === 'sandal' && !discountApplies && (unlockAtAnchorQty ?? 0) > anchorQty;

  return (
    <div
      className="rounded-xl border border-border-default bg-surface-raised p-4"
      data-testid="reorder-cross-box"
    >
      <p className="text-xs uppercase tracking-wider text-text-muted mb-2">
        {t('reorder_cross_box_title', 'Add to your order')}
      </p>
      <div className="flex items-start gap-3">
        <ImageRotator srcs={IMAGES[crossKind]} alt={label} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{label}</p>
          {/* Price: discounted (with struck full) when the discount applies,
              otherwise the full price plus, for sandals, the unlock hint. */}
          <p className="mt-0.5 text-sm" dir="ltr">
            {discountApplies && fullAed && fullAed > unitAed ? (
              <>
                <span className="text-text-muted line-through me-1" aria-label={t('reorder_cross_was_price', 'Was {{amount}} AED', { amount: formatAed(fullAed) })}>
                  {formatAed(fullAed)}
                </span>
                <span className="font-bold text-brand-700">{formatAed(unitAed)} AED</span>
                {pct > 0 ? (
                  <span className="ms-1 text-xs font-semibold text-success-700">
                    {t('reorder_cross_pct_off', '{{pct}}% off', { pct })}
                  </span>
                ) : null}
              </>
            ) : (
              <span className="font-bold text-text-primary">{formatAed(unitAed)} AED</span>
            )}
            <span className="ms-1 text-xs text-text-muted">
              {t('reorder_cross_per_pair', 'per pair, VAT included')}
            </span>
          </p>
          {canUnlock ? (
            <p className="mt-1 text-xs text-brand-700">
              {t(
                'reorder_cross_unlock_hint',
                'Add a second pair of insoles to unlock {{pct}}% off your sandals.',
                { pct },
              )}
            </p>
          ) : null}

          {/* Add-on quantity stepper 0..max (0 = not added). */}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCrossQty(Math.max(0, crossQty - 1))}
              disabled={crossQty <= 0}
              aria-label={t('reorder_cross_qty_decrease', 'Remove one pair')}
              className="h-8 w-8 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-cross-qty-minus"
            >
              −
            </button>
            <span
              className="min-w-[2rem] text-center text-sm font-semibold tabular-nums"
              data-testid="reorder-cross-qty-value"
              aria-live="polite"
            >
              {crossQty}
            </span>
            <button
              type="button"
              onClick={() => setCrossQty(Math.min(maxQty, crossQty + 1))}
              disabled={crossQty >= maxQty}
              aria-label={t('reorder_cross_qty_increase', 'Add one pair')}
              className="h-8 w-8 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-cross-qty-plus"
            >
              +
            </button>
            {crossQty > 0 ? (
              <span dir="ltr" className="ms-auto text-sm font-bold tabular-nums" data-testid="reorder-cross-line-total">
                {formatAed(Math.round(unitAed * crossQty * 100) / 100)} AED
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
